import Hotjar from '@hotjar/browser';
import React, { useEffect } from 'react';
import { Helmet, HelmetProps } from 'react-helmet-async';
import { useMatch } from 'react-router-dom';

import { pendo } from '@portals/analytics';
import { useAuth } from '@portals/redux';

import { HOTJAR_NONCE, useCSP } from '../hooks/csp';

interface MetaProps {
  title: HelmetProps['title'];
  links?: HelmetProps['link'];
  meta?: HelmetProps['meta'];
  gtmId?: string;
  hotJarId?: string;
}

export function Meta({ title, links, meta = [], gtmId, hotJarId }: MetaProps) {
  const cspContent = useCSP();
  const auth = useAuth();
  const isSignInScreen = useMatch('/auth/sign-in');

  useEffect(
    function initHotjar() {
      // Disable hotjar for internal users (example@xyte.io)
      const enableHotjar = hotJarId && auth && !auth.email.includes('@xyte.io');

      if (enableHotjar) {
        Hotjar.init(Number(hotJarId), 6, { nonce: HOTJAR_NONCE });
      }
    },
    [auth, hotJarId]
  );

  useEffect(() => {
    if (isSignInScreen && pendo.isSendingEvents()) {
      pendo.stopSendingEvents();
    } else if (!isSignInScreen && !pendo.isSendingEvents()) {
      pendo.startSendingEvents();
    }
  }, [isSignInScreen]);

  return (
    <Helmet defer={false} title={title}>
      <meta
        key="CSP"
        httpEquiv="Content-Security-Policy"
        content={cspContent}
      />

      {/* Google Tag Manager */}
      {gtmId ? (
        <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');`}</script>
      ) : null}
      {/* Google Tag Manager */}

      {/* Launchnotes-embed */}
      <script
        src="https://embed.launchnotes.io/latest/dist/esm/launchnotes-embed.js"
        async={false}
        type="module"
      />
      {/* Launchnotes-embed */}

      {/* Pendo Tag Manager */}
      <script>{`(function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
          v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
          y=e.createElement(n);y.async=!0;y.src='https://content.overview.xyte.io/agent/static/'+apiKey+'/pendo.js';
          z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
      })('0a263a3d-3c2a-43f0-566c-95cd54fbd959');`}</script>
      {/* Pendo Tag Manager */}

      {meta.map((props, index) => (
        <meta {...props} key={index} />
      ))}

      {links ? (
        links.map((props, index) => <link key={index} {...props} />)
      ) : (
        <link href="favicon.ico" rel="icon" sizes="192x192" />
      )}
    </Helmet>
  );
}
